import { ThingActivity, ThingEventSnapshot } from '@eagle/core-data-types';
import { Popover, Stack, styled, Tooltip, Typography } from '@ethings/ui-toolkit';
import { Box, useTheme } from '@mui/material';
import { DateTime } from 'luxon';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import UAParser from 'ua-parser-js';
import { Nullable, Undefinable } from '../../types';
import { useEventDialog } from '../event-dialog';
import { FormatTimestamp } from '../format';
import { EventLocationData } from '../map';
import { EventBreadcrumbPopover } from '../map/thing-event-pane/event-breadcrumb-popover';
import { useNotableEventsContext } from './notable-events-context';
import { getActivityNotableEvents, getEventDictionary } from './util';

const isIOS = (): boolean => {
  if (typeof window === 'undefined') return false;

  const parser = new UAParser();
  const result = parser.getResult();
  return result.os.name === 'iOS' ||
    (result.os.name === 'Mac OS' && navigator.maxTouchPoints > 1);
};

interface ActivityCardTimelineProps {
  activity: ThingActivity;
  events: ThingEventSnapshot[];
}

interface TimelineMarkerProps {
  color: string;
  float?: boolean;
  onClick: (event: ThingEventSnapshot) => void;
  percent: number;
  thingEvent: ThingEventSnapshot;
}

interface TimelineMarker {
  color: string;
  percent: number;
  thingEvent: ThingEventSnapshot;
}

interface TimelineMarkerGroupProps {
  markers: TimelineMarker[];
  onClick: (event: ThingEventSnapshot) => void;
}

const StyledPopover = styled(Popover)({
  pointerEvents: 'none',
});

const MIN_OVERLAPPED_PERCENT = 0.5; // %

const TimelineMarkerGroup: FC<TimelineMarkerGroupProps> = ({ markers, onClick }) => (
  <Stack
    direction="row"
    margin="0px !important" alignItems="center" position="absolute" left={`${markers[0].percent}%`} top={0} height="100%"
  >
    {markers.map((item) => {
      return (
        <TimelineMarker key={item.thingEvent.eventId} {...item} float onClick={onClick} />
      );
    })}
  </Stack>
);

const TimelineMarker: FC<TimelineMarkerProps> = ({ color, float, onClick, percent, thingEvent }) => {
  const [anchorEl, setAnchorEl] = useState<Nullable<HTMLElement>>(null);
  const open = Boolean(anchorEl);
  const isAppleDevice = isIOS();

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = (): void => {
    setAnchorEl(null);
  };

  const handleTouchStart = (event: React.TouchEvent<HTMLElement>): void => {
    event.stopPropagation();
    onClick(thingEvent);
  };

  return <>
    <StyledPopover
      anchorEl={anchorEl}
      open={open}
      onClick={(e) => { e.stopPropagation(); }}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      disableRestoreFocus
      closeAfterTransition
    >
      <Box p={'0.5rem 1rem'}>
        <EventBreadcrumbPopover
          data={[thingEvent as EventLocationData]}
          requireLocation={false}
        />
      </Box>
    </StyledPopover>
    <Box
      sx={{
        margin: '0px !important',
        width: open ? '0.4rem' : '0.15rem',
        transition: 'width 0.1s ease-in-out',
        height: '100%',
        background: color,
        ...(float
          ? {}
          : {
            position: 'absolute',
            top: 0,
            left: `${percent}%`,
          }
        ),
        cursor: 'pointer',
      }}
      onClick={() => onClick(thingEvent)}
      {...(!isAppleDevice && {
        onMouseEnter: handlePopoverOpen,
        onMouseLeave: handlePopoverClose,
      })}
      onTouchStart={handleTouchStart}
    />
  </>;
};

export const ActivityCardTimeline: FC<ActivityCardTimelineProps> = ({ activity, events }) => {
  const theme = useTheme();
  const { t } = useTranslation(['common', 'track']);
  const { notableEvents: notableEventsConfig } = useNotableEventsContext();
  const { setEvents, setEventIndex, setOpen } = useEventDialog();

  const start = DateTime.fromJSDate(activity.start).setLocale(navigator.language);
  const startLocal = start.toLocal();

  const finish = (activity.finish ? DateTime.fromJSDate(activity.finish) : DateTime.now()).setLocale(navigator.language);
  const finishLocal = finish.toLocal();

  const notableEvents = useMemo(() => {
    return getActivityNotableEvents(events, activity).map<Undefinable<TimelineMarker>>((thingEvent) => {
      const { feature, featureTypeId, eventId, eventTypeId, occurred, data: { location }, ...rest } = thingEvent;
      const eventData = getEventDictionary({ feature, featureTypeId, eventId, eventTypeId, occurred, data: { location }, ...rest }, theme, notableEventsConfig);
      if (!eventData) return;

      const duration = (activity.finish || new Date()).getTime() - activity.start.getTime();
      if (duration === 0) return;

      return {
        percent: (occurred.getTime() - activity.start.getTime()) / duration * 100,
        color: eventData.styling.backgroundColor,
        thingEvent,
      };
    }).filter((item) => !!item).sort((a, b) => a.percent - b.percent);
  }, [theme, activity, events, notableEventsConfig]);

  const groupNotableEvents = useMemo(() => {
    return notableEvents.reduce<(TimelineMarker | TimelineMarker[])[]>((arr, item) => {
      const lastItem = arr[arr.length - 1];
      if (!lastItem) {
        arr.push(item);
        return arr;
      }

      const isArray = Array.isArray(lastItem);

      if (isArray) {
        const isOverlapped = (item.percent - lastItem[lastItem.length - 1].percent) < MIN_OVERLAPPED_PERCENT;
        if (isOverlapped) {
          lastItem.push(item);
        } else arr.push(item);
        return arr;
      }

      const isOverlapped = (item.percent - lastItem.percent) < MIN_OVERLAPPED_PERCENT;
      if (isOverlapped) arr[arr.length - 1] = [lastItem, item];
      else arr.push(item);
      return arr;
    }, []);
  }, [notableEvents]);

  const onItemClick = useCallback((event: ThingEventSnapshot) => {
    const index = notableEvents.findIndex((item) => item.thingEvent.eventId === event.eventId);
    if (index !== -1) {
      setEventIndex(index);
      setEvents(notableEvents.map((item) => item.thingEvent));
      setOpen(true);
    }
  }, [notableEvents, setEventIndex, setEvents, setOpen]);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography color={theme.palette.text.secondary} variant="caption">
          {startLocal.toFormat('dd LLLL')}
        </Typography>
        <Typography color={theme.palette.text.secondary} variant="caption">
          Timeline
        </Typography>
        <Typography color={theme.palette.text.secondary} variant="caption">
          {finishLocal.toFormat('dd LLLL')}
        </Typography>
      </Stack>

      <Box
        sx={{
          height: '18px',
          borderRadius: '18px',
          border: 'solid 2px rgba(0, 0, 0, 0.12)',
          width: '100%',
          padding: '0 10px',
        }}
      >
        <Stack
          direction="row"
          alignItems="center" justifyContent="space-between" height="100%" width="100%" position="relative"
          spacing={1}
        >
          {groupNotableEvents.map((timelineMarkerProps) => {
            if (Array.isArray(timelineMarkerProps)) {
              return <TimelineMarkerGroup key={timelineMarkerProps[0].thingEvent.eventId} markers={timelineMarkerProps} onClick={(event) => {
                onItemClick(event);
              }} />;
            }
            return <TimelineMarker key={timelineMarkerProps.thingEvent.eventId} {...timelineMarkerProps} onClick={(event) => {
              onItemClick(event);
            }} />;
          })}
        </Stack>
      </Box>

      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Tooltip title={DateTime.fromJSDate(activity.start).toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)}>
          <Typography color={theme.palette.text.secondary} variant="caption">
            {<FormatTimestamp format="timeWithSeconds" value={activity.start} />}
          </Typography>
        </Tooltip>
        <Typography color={theme.palette.text.secondary} variant="caption">
          {notableEvents.length} {t('common:terms.event', { count: notableEvents.length }).toLowerCase()}
        </Typography>
        {activity.finish
          ? <Tooltip title={DateTime.fromJSDate(activity.finish).toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)}>
            <Typography color={theme.palette.text.secondary} variant="caption">
              <FormatTimestamp format="timeWithSeconds" value={activity.finish} />
            </Typography>
          </Tooltip>
          : <Typography color={theme.palette.text.secondary} variant="caption">Ongoing...</Typography>
        }
      </Stack>
    </Box>
  );
};
